/**
 * Reads the signed-in person's locale and time zone from GET /v1/users/me and
 * hands them to i18n. Only the person's own choice can switch the interface
 * to Arabic (README.md, "The rule").
 *
 * The API says which locale is theirs (D28): `personalLocale` is
 * TenantUser.locale, the person's own choice, or null when they have made
 * none; `locale` is that choice or, when null, the workspace's. So the same
 * answer reaches every device, including a choice that equals the
 * workspace's own locale, with nothing remembered on the device and no
 * second request for the workspace's settings.
 *
 * It goes through the query cache under the key ProfilePage uses, and listens
 * to it: saving the profile invalidates ["me"], the refetch lands here, and
 * the interface switches language without a reload.
 */
import type { QueryClient } from "@tanstack/react-query";
import { get } from "@/lib/api/client";
import { tenantKey } from "@/lib/api/hooks";
import { i18n } from "./core";
import { canonicalLocale } from "./locale";

/** The fields of GET /v1/users/me this reads. */
interface Me {
  locale?: unknown;
  personalLocale?: unknown;
  timezone?: unknown;
}

const sameKey = (a: readonly unknown[], b: readonly unknown[]) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * What the interface takes from one answer. A missing or unreadable
 * `personalLocale` counts as no choice: the interface stays in the device's
 * language or English, never guessed from `locale` (opt-in, charter
 * 2026-10-10). With no choice, `locale` is the workspace's and supplies only
 * the formatting conventions; with one, the workspace's is not needed.
 */
function apply(me: Me | undefined): void {
  if (!me || typeof me.locale !== "string") return;
  const person = canonicalLocale(me.personalLocale);
  i18n.setUserPreferences({
    locale: person,
    workspaceLocale: person ? null : me.locale,
    timeZone: me.timezone,
  });
}

/** Starts syncing; returns the function that stops listening. */
export function syncPersonLocale(queryClient: QueryClient): () => void {
  const meKey = tenantKey(["me"]);

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || !sameKey(event.query.queryKey, meKey))
      return;
    apply(event.query.state.data as Me | undefined);
  });

  // A workspace that is not Active answers 403 here as on every route; the
  // API client records that, and the device's choice stays in use.
  void queryClient
    .fetchQuery({
      queryKey: meKey,
      queryFn: () => get<Me>("/users/me"),
      staleTime: 15_000,
    })
    .then(apply, () => undefined);
  return unsubscribe;
}
